import MaterialTable, { Column, MTableBody } from "material-table";
import React, { useEffect, useMemo, useState } from "react";
import { arrayToLookup, getStickyColumns, localization, modifyDefaultOptions, tableIconsModified } from "../../../helpers/table";
import ProgramBuysFooter from "./ProgramBuysFooter";
import { number } from "prop-types";
import FilterMultiSelect from "../../../components/table/FilterMultiSelect";
import useAPI from "../../../hooks/useAPI";
import moment from "moment";
import LoadingTable from "../../../components/table/LoadingTable";

const sumFormatter = new Intl.NumberFormat('ru', {style: 'decimal', minimumFractionDigits: 2})

export default function AttractedBudgetTable({year, type}: {year: number, type: 'allLevels' | 'attracted'}){
  const api = useAPI();

  const [data, setData] = useState<AttractedBudgetRow[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  console.log(data.filter(item => !!item.currentAmendment));
  

  const lookups = useMemo<AttractedBudgetLookups>(() => {
    const keys: (keyof AttractedBudgetData)[] = ['subject', 'parentName']
    return keys.reduce((acc, key) => ({...acc, [key]: arrayToLookup(key, data)}), {})
  }, [data])

  const stickyColumns = useMemo<Column<AttractedBudgetRow>[]>(() => {
    const columns: Column<AttractedBudgetRow>[] = [
      {
        title: '№ п/п',
        field: 'order',
        width: 80,
        cellStyle: { minWidth: 80 },
      },
      {
        title: '№, дата договора',
        field: 'contractNum',
        width: 190,
        filtering: true,
        cellStyle: { minWidth: 190 },
        render: (rowData) => (<div>
          <p style={{marginBottom: 0}}>{rowData.contractNum}</p>
          {rowData.currentAmendment && <p>
            доп. соглашение №{rowData.currentAmendment.num} от {moment(rowData.currentAmendment.regDate).format('DD.MM.YYYY')}
          </p>}
        </div>)
      },
      {
        title: 'Контрагент',
        field: 'contragent',
        width: 200,
        filtering: true,
        cellStyle: { minWidth: 200 },
      },
    ]
    return getStickyColumns(columns, 0)
  }, [])

  useEffect(() => {
    const fetchData = async() => {
      const data: AttractedBudgetData[] = type === 'attracted' ?
       (await api.get(`/reports/budget/funds/incoming?year=${year}`)).data :
       (await api.get(`/reports/budget/funds/all?year=${year}`)).data
      setData(data.map((row, i) => ({
        ...row,
        order: i + 1,
        contractNum: `${row.num} от ${moment(new Date(row.regDate)).format('DD.MM.YYYY')}`,
        contragent: row.contractOrg?.title || '',
        contragentInn: row.contractOrg?.inn || '',
        acts: row.paymentActs.map(act => `${act.num} от ${moment(new Date(act.paymentDate)).format('DD.MM.YYYY')}`),
        orders: row.paymentOrders.map(order => `${order.num} от ${moment(new Date(order.paymentDate)).format('DD.MM.YYYY')}`)
      })))
      setIsLoading(false)
    }
    fetchData()
  }, [year, type])

  return (
    <div className="mt-4">
      <h2 style={{fontSize: 16, color: '#212529', textAlign: 'left'}}>
        Реестр договоров и иных документов, подтверждающих привлечение средств внебюджетных источников на проведение прикладных научных исследований и (или) экспериментальных разработок, полученных от заказчиков (иных внешних партнеров)
      </h2>
      <LoadingTable loadingObject={{ isLoading }}>
        <MaterialTable<AttractedBudgetRow>
          data={data}
          columns={[
            ...stickyColumns,
            {
              title: 'ИНН контрагента',
              field: 'contragentInn'
            },
            {
              title: 'Предмет договора',
              field: 'subject',
              lookup: lookups.subject,
              cellStyle: { minWidth: 230 },
              filterCellStyle: { maxWidth: 230 },
              filterComponent: FilterMultiSelect
            } as Column<AttractedBudgetRow>,
            {
              title: 'Сумма договора, руб.',
              field: 'contractSum',
              aggregated: true,
              render: rowData => <div>{sumFormatter.format(rowData.contractSum)}</div>
            } as Column<AttractedBudgetRow>,
            {
              title: `Сумма договора на ${year} год, руб.`,
              field: 'contractYearSum',
              aggregated: true,
              render: rowData => <div>{sumFormatter.format(rowData?.contractYearSum ? rowData?.contractYearSum : 0)}</div>
            } as Column<AttractedBudgetRow>,
            {
              title: 'Полученный результат',
              field: 'result',
              cellStyle: { minWidth: 230 }
            },
            {
              hidden: type === 'allLevels',
              title: 'Реквизиты актов',
              field: 'paymentActs',
              cellStyle: { minWidth: 260 },
              render: rowData => <div className="d-flex flex-column gap-1 m-0">
                {rowData.acts.map(act => <p className="m-0">{act},<br/></p>)}
              </div>,
              customFilterAndSearch: (filterValue, rowData) => rowData.acts.join('').includes(filterValue)
            },
            {
              hidden: type === 'allLevels',
              title: 'Сумма актов, руб.',
              field: 'paymentActSum',
              aggregated: true,
              render: rowData => <div>{sumFormatter.format(rowData.paymentActSum)}</div>
            }  as Column<AttractedBudgetRow>,
            {
              title: 'Реквизиты платежных поручений',
              field: 'paymentOrders',
              cellStyle: { minWidth: 200 },
              render: rowData => <div className="d-flex flex-column gap-1 m-0">
                {rowData.orders.map(order => <p className="m-0">{order},<br/></p>)}
              </div>,
              customFilterAndSearch: (filterValue, rowData) => rowData.orders.join('').includes(filterValue)
            },
            {
              title: 'Сумма поступлений, руб.',
              field: 'paymentOrderSum',
              aggregated: true,
              render: rowData => <div>{sumFormatter.format(rowData.paymentOrderSum)}</div>
            }  as Column<AttractedBudgetRow>,
            {
              title: 'Стратегический проект или раздел научно-исследовательской политики Программы развития университета, в рамках реализации которого (ых) поступили денежные средства',
              field: 'parentName',
              lookup: lookups.parentName,
              cellStyle: { minWidth: 500 },
              filterCellStyle: { maxWidth: 500 },
              filterComponent: FilterMultiSelect
            } as Column<AttractedBudgetRow>,
          ]}
          options={modifyDefaultOptions({
            toolbar: false,
            sorting: false,
            headerStyle: { fontFamily: 'inherit', position: 'sticky', backgroundColor: 'white', zIndex: 10 },
            filterCellStyle: { position: 'sticky', top: 71.5, backgroundColor: 'white' },
            maxBodyHeight: 'calc(100vh - 450px)',
            filtering: true
          })}
          components={{
              Body: (props) =>  <>
                  <MTableBody {...props}/>
                  <ProgramBuysFooter {...props} colSpans={{contractYearSum: 3, paymentActSum: 2}}/>
                </>
              ,
          }}
          localization={localization}
          icons={tableIconsModified}
        />
      </LoadingTable>
    </div>
  )
}

interface AttractedBudgetData{
  order: number,
  contractOrg: IContractOrg,
  num: string,
  regDate: string,
  subject: string,
  contractSum: number,
  contractYearSum: number,
  result: string,
  paymentActs: IPaymentEntity[],
  paymentActSum: number,
  paymentOrders: IPaymentEntity[],
  paymentOrderSum: number,
  parentName: string,
  currentAmendment?: any
}

interface IContractOrg{
  id: string,
  inn: string,
  kpp: string,
  title: string
}

interface IPaymentEntity{
  num: string,
  paymentDate: string
}

interface AttractedBudgetRow extends AttractedBudgetData{
  order: number,
  contractNum: string,
  contragent: string,
  contragentInn: string,
  acts: string[],
  orders: string[]
}

 type AttractedBudgetLookups = {
    [key in keyof AttractedBudgetData]?: {[key: string]: string} | undefined;
  };
